import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { PlaybookCard } from '../../components/experiments/PlaybookCard'
import { PageHeader } from '../../components/PageHeader'
import { Card, EmptyState, LoadMore, Pill, ProblemNotice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import type { components } from '../../lib/api.types'
import { getPlaybook, getResult, listRuns, type ExperimentRun } from '../../lib/experiments/api'
import { formatDateTime } from '../../lib/format'
import { useFanOut, type FanOutEntry } from '../../lib/useFanOut'
import { usePagedList } from '../../lib/usePagedList'
import { useResource } from '../../lib/useResource'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { NotFound } from '../NotFound'
import '../../styles/experiments-playbook.css'

type Entry = components['schemas']['PlaybookEntry']
type Result = components['schemas']['ExperimentResult']

const STAGES: Record<ExperimentRun['stage'], string> = {
  awaiting_before: 'Before reading', activity_in_progress: 'Activity in progress', settling: 'Settle',
  awaiting_after: 'After reading', awaiting_report: 'How you feel', complete: 'Complete', incomplete: 'Incomplete', cancelled: 'Cancelled',
}

/** An account change abandons page reads and every expanded activity's run list. */
export function Experiments() {
  const { account } = useAuth()
  if (account === null) return <p role="status">Loading your playbook…</p>
  if (account.experiments_enabled_for_me !== true) return <NotFound />
  return <ExperimentsBody key={account.user_id} userId={account.user_id} />
}

function ExperimentsBody({ userId }: { userId: string }) {
  const fetchPage = useCallback((cursor: string | null, signal: AbortSignal) =>
    getPlaybook(userId, { ...(cursor === null ? {} : { cursor }), signal }), [userId])
  const read = usePagedList(fetchPage)
  const [landing, armLanding] = useRetryLanding(read.error !== null)
  return <div className="stack stack--wide playbook" data-testid="playbook-screen">
    <PageHeader title="Your playbook" eyebrow="Experiments" ref={landing} tabIndex={-1}
      lead="What you've tried, what the readings showed, what you reported, and how often it has repeated. Mixed results and runs that showed nothing stay here too."
      actions={<Link className="btn btn--primary" to="/record/experiment">Try something</Link>} />
    {read.isLoading && <p role="status">Loading your playbook…</p>}
    {read.error !== null && <ProblemNotice error={read.error} onRetry={read.nextCursor === null ? read.reload : read.loadMore}
      retryBusy={read.isLoading || read.isPaging} landing={armLanding}>Your playbook could not be read.</ProblemNotice>}
    {!read.isLoading && read.error === null && read.isComplete && read.items.length === 0 && <Card>
      <EmptyState title="Nothing here yet" align="start"><p>What you try is kept here, including incomplete runs.</p></EmptyState>
    </Card>}
    <div className="playbook__entries">
      {read.items.map((entry) => <ActivityCard key={entry.activity.key} entry={entry} userId={userId} />)}
    </div>
    {!read.isLoading && read.pagesLoaded !== 0 && <LoadMore loaded={read.items.length} nextCursor={read.nextCursor}
      isLoading={read.isPaging} onLoadMore={read.loadMore} noun="activity card" />}
    <PlaybookExplainers />
  </div>
}

function ActivityCard({ entry, userId }: { entry: Entry; userId: string }) {
  const [open, setOpen] = useState(false)
  return <PlaybookCard entry={entry} onOpen={setOpen}>
    {open && <PlaybookRuns userId={userId} activity={entry.activity.key} />}
  </PlaybookCard>
}

function PlaybookRuns({ userId, activity }: { userId: string; activity: Entry['activity']['key'] }) {
  const load = useCallback(async (signal: AbortSignal) => {
    const runs = await listRuns(userId, { activity, signal })
    if (runs.some((run) => run.user_id !== userId || run.activity_key !== activity)) {
      throw new Error('The runs did not match the requested playbook activity.')
    }
    return runs
  }, [userId, activity])
  const read = useResource(load)
  const [landing, armLanding] = useRetryLanding(read.error !== null)
  if (read.isLoading || read.data === null) return <div className="playbook__run-body" ref={landing} tabIndex={-1}>
    {read.error !== null
      ? <ProblemNotice error={read.error} onRetry={read.reload} retryBusy={read.isLoading} landing={armLanding}>The runs could not be read.</ProblemNotice>
      : <p role="status">Loading runs…</p>}
  </div>
  return <div className="playbook__run-body" ref={landing} tabIndex={-1}><RunRows runs={read.data} /></div>
}

function RunRows({ runs }: { runs: ExperimentRun[] }) {
  const byId = useMemo(() => new Map(runs.map((run) => [run.id, run])), [runs])
  const fetchOne = useCallback(async (id: string, signal: AbortSignal) => {
    const run = byId.get(id)!
    const result = await getResult(id, { signal })
    if (result.comparison.before_recording_id !== run.before_recording_id || result.comparison.after_recording_id !== run.after_recording_id) {
      throw new Error('The result did not match the requested run.')
    }
    return result
  }, [byId])
  const eligible = useCallback((id: string) => {
    const run = byId.get(id)!
    return run.stage === 'complete' && run.report_state !== 'pending' && run.result_revision != null
  }, [byId])
  const results = useFanOut(runs.map((run) => run.id), fetchOne, { eligible })
  if (runs.length === 0) return <p>No runs are available for this activity.</p>
  return <ul className="playbook__run-list">{runs.map((run) => <li key={run.id} className="playbook__run">
    <div className="playbook__run-head">
      <time dateTime={run.created_at}>{formatDateTime(run.created_at)}</time>
      <Pill>{STAGES[run.stage]}</Pill><Pill>Report · {run.report_state}</Pill>
    </div>
    <RunComparison run={run} read={results.entries[run.id]} retry={() => results.retry(run.id)} />
    {Boolean(run.attempts?.length) && <ul className="playbook__attempts" aria-label="Capture attempts">
      {run.attempts!.map((attempt) => <li key={`${attempt.capture_role}/${attempt.attempt_index}`}>
        {attempt.capture_role} · {attempt.status}{attempt.loss_reason ? ` · ${attempt.loss_reason}` : ''}
        {attempt.recording_id && <> · <Link to={`/history/${encodeURIComponent(attempt.recording_id)}`}>Open reading</Link></>}
      </li>)}
    </ul>}
  </li>)}</ul>
}

function RunComparison({ run, read, retry }: { run: ExperimentRun; read?: FanOutEntry<Result>; retry: () => void }) {
  const [landing, armLanding] = useRetryLanding(read?.status === 'error')
  if (run.stage !== 'complete' || run.report_state === 'pending' || run.result_revision == null) {
    return <p className="playbook__muted">{run.stage === 'incomplete' || run.stage === 'cancelled' ? 'Nothing is compared.' : 'Result · closed'}</p>
  }
  if (read?.status === 'error') return <ProblemNotice error={read.error} onRetry={retry} landing={armLanding}
    retryLabel="Retry comparison">The comparison could not be read.</ProblemNotice>
  if (read?.status !== 'ready' || read.value === undefined) return <p role="status">Loading comparison…</p>
  const comparison = read.value.comparison
  return <div className="playbook__run-comparison" ref={landing} tabIndex={-1}>
    <Pill tone={comparison.category === 'comparable' ? 'green' : 'amber'}>{comparison.category === 'comparable' ? 'Comparable' : 'Not comparable'}</Pill>
    {comparison.category !== 'comparable' && <div className="playbook__reasons">
      <p>{comparison.text}</p>
      {comparison.validity.filter((check) => check.passed === false).map((check) => <p key={check.check}>{check.text}</p>)}
      {comparison.reasons.map((reason) => <span key={reason}>{reason}</span>)}
    </div>}
    <Link className="btn btn--secondary btn--sm" to={`/experiment-runs/${encodeURIComponent(run.id)}`}>Open result</Link>
  </div>
}

/** Fixed copy from the three foot panels of Playbook.dc.html. */
export function PlaybookExplainers() {
  return <Card as="section" flat className="playbook__explainers" aria-label="How to read your playbook">
    <div><h2 className="playbook__label">How a pattern is counted</h2><p>One run is descriptive. Runs on the same day count once. A pattern needs distinct days, the control days, and the fixed protocol; the label “consistent so far” is the strongest this page says until the comparison protocol is frozen and met.</p></div>
    <div><h2 className="playbook__label">Three columns, never one</h2><p>Readings, reports and repeats are kept apart on purpose. A reading can change without you feeling different, and you can feel better with no change in a reading. The playbook never ranks an activity as good for you.</p></div>
    <div><h2 className="playbook__label">What leaves this page</h2><p>Nothing here enters your baseline or your daily note. Export and deletion cover every run, reading and report.</p></div>
  </Card>
}
